import { defineStore } from 'pinia'
// @ts-ignore
import { ref, computed } from 'vue'
import tenderApplicationService from '@/services/tenderApplication.service'
import { ApplicationStatus } from '@/types/tender'
import type {
  ApplicationResponseDto,
  ApplicationDocumentResponseDto,
  StatusHistoryResponseDto,
  CreateApplicationDto,
  UpdateApplicationDto,
  UpdateApplicationStatusDto,
  UpdateDocumentDto,
  PageResponseDto,
  ApplicationQueryParams,
  GroupedDocumentsDto
} from '@/types/tender'

/**
 * Pinia store for tender application management
 * Handles state, actions, and caching for applications and application documents
 */
export const useTenderApplicationStore = defineStore('tenderApplication', () => {
  // ============================================
  // State
  // ============================================
  
  /**
   * List of applications (paginated results)
   */
  const applications = ref<ApplicationResponseDto[]>([])
  
  /**
   * Currently selected/viewed application
   */
  const currentApplication = ref<ApplicationResponseDto | null>(null)
  
  /**
   * Documents for the current application
   */
  const currentApplicationDocuments = ref<ApplicationDocumentResponseDto[]>([])
  
  /**
   * Status history for the current application
   */
  const currentStatusHistory = ref<StatusHistoryResponseDto[]>([])
  
  /**
   * Pagination metadata
   */
  const pagination = ref({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
    last: true,
    first: true
  })
  
  /**
   * Loading states
   */
  const loading = ref(false)
  const loadingDocuments = ref(false)
  const loadingHistory = ref(false)
  const submitting = ref(false)
  
  /**
   * Error state
   */
  const error = ref<string | null>(null)
  
  /**
   * Cache for applications by ID (for quick lookups)
   */
  const applicationCache = ref<Map<number, ApplicationResponseDto>>(new Map())

  // ============================================
  // Getters
  // ============================================
  
  /**
   * Get all applications in current list
   */
  const getApplications = computed(() => applications.value)
  
  /**
   * Get current application
   */
  const getCurrentApplication = computed(() => currentApplication.value)
  
  /**
   * Get current application documents
   */
  const getCurrentApplicationDocuments = computed(() => currentApplicationDocuments.value)
  
  /**
   * Get current status history
   */
  const getCurrentStatusHistory = computed(() => currentStatusHistory.value)
  
  /**
   * Check if data is loading
   */
  const isLoading = computed(() => loading.value)
  
  /**
   * Check if documents are loading
   */
  const isLoadingDocuments = computed(() => loadingDocuments.value)
  
  /**
   * Check if history is loading
   */
  const isLoadingHistory = computed(() => loadingHistory.value)
  
  /**
   * Check if submitting data
   */
  const isSubmitting = computed(() => submitting.value)
  
  /**
   * Get current error
   */
  const getError = computed(() => error.value)
  
  /**
   * Get pagination info
   */
  const getPagination = computed(() => pagination.value)
  
  /**
   * Filter applications by status
   */
  const getApplicationsByStatus = computed(() => (status: ApplicationStatus) => {
    return applications.value.filter((app: ApplicationResponseDto) => app.status === status)
  })
  
  /**
   * Filter applications by tender ID
   */
  const getApplicationsByTender = computed(() => (tenderId: number) => {
    return applications.value.filter((app: ApplicationResponseDto) => app.tenderId === tenderId)
  })
  
  /**
   * Get shortlisted/awaiting results applications
   */
  const getShortlistedApplications = computed(() => {
    return applications.value.filter((app: ApplicationResponseDto) => 
      app.status === ApplicationStatus.AWAITING_RESULTS
    )
  })
  
  /**
   * Get awarded applications
   */
  const getAwardedApplications = computed(() => {
    return applications.value.filter((app: ApplicationResponseDto) => 
      app.status === ApplicationStatus.AWARDED
    )
  })
  
  /**
   * Get application from cache by ID
   */
  const getApplicationFromCache = computed(() => (id: number) => {
    return applicationCache.value.get(id)
  })

  // ============================================
  // Actions
  // ============================================
  
  /**
   * Fetch applications created by current user
   */
  async function fetchMyApplications(params?: ApplicationQueryParams) {
    loading.value = true
    error.value = null
    
    try {
      const response = await tenderApplicationService.getMyApplications(params)
      
      if (response) {
        applications.value = response.content
        pagination.value = {
          page: response.page,
          size: response.size,
          totalElements: response.totalElements,
          totalPages: response.totalPages,
          last: response.last,
          first: response.first
        }
        
        // Update cache
        response.content.forEach((app: ApplicationResponseDto) => {
          applicationCache.value.set(app.id, app)
        })
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch applications'
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Fetch applications for a specific tender
   */
  async function fetchApplicationsForTender(tenderId: number) {
    loading.value = true
    error.value = null
    
    try {
      const response = await tenderApplicationService.getApplicationsByTender(tenderId)
      
      if (response) {
        // Store as the current application list
        applications.value = response
        
        // Update pagination to reflect the response (no pagination from backend for this endpoint)
        pagination.value = {
          page: 0,
          size: response.length,
          totalElements: response.length,
          totalPages: 1,
          last: true,
          first: true
        }
        
        // Update cache
        response.forEach((app: ApplicationResponseDto) => {
          applicationCache.value.set(app.id, app)
        })
      }
      
      return response
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch applications'
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Fetch a single application by ID
   */
  async function fetchApplicationById(id: number, useCache: boolean = false) {
    // Check cache first if enabled
    if (useCache && applicationCache.value.has(id)) {
      currentApplication.value = applicationCache.value.get(id)!
      return currentApplication.value
    }
    
    loading.value = true
    error.value = null
    
    try {
      const application = await tenderApplicationService.getApplicationById(id)
      
      if (application) {
        currentApplication.value = application
        applicationCache.value.set(id, application)
      }
      
      return application
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch application'
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Fetch an application by reference number
   */
  async function fetchApplicationByReference(referenceNumber: string) {
    loading.value = true
    error.value = null
    
    try {
      const application = await tenderApplicationService.getApplicationByReference(referenceNumber)
      
      if (application) {
        currentApplication.value = application
        applicationCache.value.set(application.id, application)
      }
      
      return application
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch application'
      throw err
    } finally {
      loading.value = false
    }
  }
  
  /**
   * Create a new application
   */
  async function createApplication(applicationData: CreateApplicationDto) {
    submitting.value = true
    error.value = null
    
    try {
      const application = await tenderApplicationService.createApplication(applicationData)
      
      if (application) {
        // Add to list and cache
        applications.value.unshift(application)
        applicationCache.value.set(application.id, application)
        currentApplication.value = application
      }
      
      return application
    } catch (err: any) {
      error.value = err.message || 'Failed to create application'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Update an existing application
   */
  async function updateApplication(id: number, applicationData: UpdateApplicationDto) {
    submitting.value = true
    error.value = null
    
    try {
      const updated = await tenderApplicationService.updateApplication(id, applicationData)
      
      if (updated) {
        // Update in list
        const index = applications.value.findIndex((a: ApplicationResponseDto) => a.id === id)
        if (index !== -1) {
          applications.value[index] = updated
        }
        
        // Update cache
        applicationCache.value.set(id, updated)
        
        // Update current if it's the same application
        if (currentApplication.value?.id === id) {
          currentApplication.value = updated
        }
      }
      
      return updated
    } catch (err: any) {
      error.value = err.message || 'Failed to update application'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Delete an application
   */
  async function deleteApplication(id: number) {
    submitting.value = true
    error.value = null
    
    try {
      await tenderApplicationService.deleteApplication(id)
      
      // Remove from list
      applications.value = applications.value.filter((a: ApplicationResponseDto) => a.id !== id)
      
      // Remove from cache
      applicationCache.value.delete(id)
      
      // Clear current if it's the deleted application
      if (currentApplication.value?.id === id) {
        currentApplication.value = null
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to delete application'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Mark application as ready for submission
   */
  async function markReadyForSubmission(id: number) {
    submitting.value = true
    error.value = null
    
    try {
      const updated = await tenderApplicationService.markReadyForSubmission(id)
      
      if (updated) {
        updateApplicationInState(id, updated)
      }
      
      return updated
    } catch (err: any) {
      error.value = err.message || 'Failed to mark application as ready'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Submit an application
   */
  async function submitApplication(id: number) {
    submitting.value = true
    error.value = null
    
    try {
      const updated = await tenderApplicationService.submitApplication(id)
      
      if (updated) {
        updateApplicationInState(id, updated)
      }
      
      return updated
    } catch (err: any) {
      error.value = err.message || 'Failed to submit application'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Update application status (for evaluators)
   */
  async function updateStatus(id: number, statusData: UpdateApplicationStatusDto) {
    submitting.value = true
    error.value = null
    
    try {
      const updated = await tenderApplicationService.updateApplicationStatus(id, statusData)
      
      if (updated) {
        updateApplicationInState(id, updated)
      }
      
      return updated
    } catch (err: any) {
      error.value = err.message || 'Failed to update status'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Fetch status history for an application
   */
  async function fetchStatusHistory(applicationId: number) {
    loadingHistory.value = true
    error.value = null
    
    try {
      const history = await tenderApplicationService.getStatusHistory(applicationId)
      
      if (history) {
        currentStatusHistory.value = history
      }
      
      return history
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch status history'
      throw err
    } finally {
      loadingHistory.value = false
    }
  }
  
  /**
   * Fetch documents for an application
   */
  async function fetchApplicationDocuments(applicationId: number) {
    loadingDocuments.value = true
    error.value = null
    
    try {
      const documents = await tenderApplicationService.getApplicationDocuments(applicationId)
      
      if (documents) {
        currentApplicationDocuments.value = documents
      }
      
      return documents
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch documents'
      throw err
    } finally {
      loadingDocuments.value = false
    }
  }
  
  /**
   * Fetch documents grouped by type
   */
  async function fetchGroupedDocuments(applicationId: number) {
    loadingDocuments.value = true
    error.value = null
    
    try {
      const grouped = await tenderApplicationService.getGroupedDocuments(applicationId)
      return grouped
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch grouped documents'
      throw err
    } finally {
      loadingDocuments.value = false
    }
  }
  
  /**
   * Upload a document to an application
   */
  async function uploadDocument(
    applicationId: number,
    file: File,
    documentType: string,
    description?: string,
    tags?: string
  ) {
    submitting.value = true
    error.value = null
    
    try {
      const document = await tenderApplicationService.uploadDocument(
        applicationId,
        file,
        documentType,
        description,
        tags
      )
      
      if (document) {
        currentApplicationDocuments.value.push(document)
      }
      
      return document
    } catch (err: any) {
      error.value = err.message || 'Failed to upload document'
      throw err
    } finally {
      submitting.value = false
    }
  }

  /**
   * Upload multiple documents to an application in batch (optimized)
   */
  async function uploadDocumentsBatch(
    applicationId: number,
    files: File[],
    documentType: string,
    description?: string,
    tags?: string
  ) {
    submitting.value = true
    error.value = null
    
    try {
      const documents = await tenderApplicationService.uploadDocumentsBatch(
        applicationId,
        files,
        documentType,
        description,
        tags
      )
      
      if (documents) {
        currentApplicationDocuments.value.push(...documents)
      }
      
      return documents
    } catch (err: any) {
      error.value = err.message || 'Failed to upload documents'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Delete an application document
   */
  async function deleteDocument(documentId: number) {
    submitting.value = true
    error.value = null
    
    try {
      await tenderApplicationService.deleteDocument(documentId)
      
      // Remove from current documents
      currentApplicationDocuments.value = currentApplicationDocuments.value.filter(
        (doc: ApplicationDocumentResponseDto) => doc.id !== documentId
      )
    } catch (err: any) {
      error.value = err.message || 'Failed to delete document'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Download an application document
   */
  async function downloadDocument(documentId: number, fileName: string) {
    console.log('[STORE] Download application document requested:', { documentId, fileName })
    
    try {
      console.log('[STORE] Calling tenderApplicationService.downloadDocument...')
      const blob = await tenderApplicationService.downloadDocument(documentId)
      
      console.log('[STORE] Blob received:', {
        type: blob?.type,
        size: blob?.size,
        isBlob: blob instanceof Blob
      })
      
      if (blob) {
        console.log('[STORE] Creating download link...')
        // Create download link
        const url = window.URL.createObjectURL(blob)
        console.log('[STORE] Blob URL created:', url)
        
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        
        console.log('[STORE] Triggering download click...')
        link.click()
        
        // Cleanup
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        console.log('[STORE] Download completed successfully')
      } else {
        console.error('[STORE] No blob received from service')
        throw new Error('No blob received')
      }
    } catch (err: any) {
      console.error('[STORE] Download failed:', {
        error: err,
        message: err.message,
        stack: err.stack,
        response: err.response
      })
      error.value = err.message || 'Failed to download document'
      throw err
    }
  }
  
  /**
   * Update document metadata
   */
  async function updateDocumentMetadata(documentId: number, updateData: UpdateDocumentDto) {
    submitting.value = true
    error.value = null
    
    try {
      const updated = await tenderApplicationService.updateDocument(documentId, updateData)
      
      if (updated) {
        // Update in current documents
        const index = currentApplicationDocuments.value.findIndex(
          (doc: ApplicationDocumentResponseDto) => doc.id === documentId
        )
        if (index !== -1) {
          currentApplicationDocuments.value[index] = updated
        }
      }
      
      return updated
    } catch (err: any) {
      error.value = err.message || 'Failed to update document'
      throw err
    } finally {
      submitting.value = false
    }
  }
  
  /**
   * Helper: Update application in all state locations
   */
  function updateApplicationInState(id: number, updated: ApplicationResponseDto) {
    // Update in list
    const index = applications.value.findIndex((a: ApplicationResponseDto) => a.id === id)
    if (index !== -1) {
      applications.value[index] = updated
    }
    
    // Update cache
    applicationCache.value.set(id, updated)
    
    // Update current if it's the same application
    if (currentApplication.value?.id === id) {
      currentApplication.value = updated
    }
  }
  
  /**
   * Set current application (without fetching)
   */
  function setCurrentApplication(application: ApplicationResponseDto | null) {
    currentApplication.value = application
    if (application) {
      applicationCache.value.set(application.id, application)
    }
  }
  
  /**
   * Clear current application
   */
  function clearCurrentApplication() {
    currentApplication.value = null
    currentApplicationDocuments.value = []
    currentStatusHistory.value = []
  }
  
  /**
   * Clear all applications
   */
  function clearApplications() {
    applications.value = []
    pagination.value = {
      page: 0,
      size: 20,
      totalElements: 0,
      totalPages: 0,
      last: true,
      first: true
    }
  }
  
  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }
  
  /**
   * Clear cache
   */
  function clearCache() {
    applicationCache.value.clear()
  }

  // ============================================
  // Return public API
  // ============================================
  
  return {
    // State
    applications,
    currentApplication,
    currentApplicationDocuments,
    currentStatusHistory,
    pagination,
    loading,
    loadingDocuments,
    loadingHistory,
    submitting,
    error,
    
    // Getters
    getApplications,
    getCurrentApplication,
    getCurrentApplicationDocuments,
    getCurrentStatusHistory,
    isLoading,
    isLoadingDocuments,
    isLoadingHistory,
    isSubmitting,
    getError,
    getPagination,
    getApplicationsByStatus,
    getApplicationsByTender,
    getShortlistedApplications,
    getAwardedApplications,
    getApplicationFromCache,
    
    // Actions
    fetchMyApplications,
    fetchApplicationsForTender,
    fetchApplicationById,
    fetchApplicationByReference,
    createApplication,
    updateApplication,
    deleteApplication,
    markReadyForSubmission,
    submitApplication,
    updateStatus,
    fetchStatusHistory,
    fetchApplicationDocuments,
    fetchGroupedDocuments,
    uploadDocument,
    uploadDocumentsBatch,
    downloadDocument,
    deleteDocument,
    updateDocumentMetadata,
    setCurrentApplication,
    clearCurrentApplication,
    clearApplications,
    clearError,
    clearCache
  }
})
